<template>
  <div>
    <p class="font--p-3">주민등록증 인증</p>
    <v-row class="file-upload" no-gutters>
      <v-col cols="12">
        <SkeletonIdLoader :is-active="!!idImage" />
      </v-col>
      <v-col cols="12">
        <p v-show="!!idImage" class="mt-3 mb-1 error--text font-size--16">
          ※ 개인정보 보호를 위해 미리보기가 제공되지 않습니다
        </p>
      </v-col>
      <v-col v-if="idImage" cols="12" class="d-flex justify-center my-3 font-size--15">
        <label class="mr-2 font-weight-bold" for="id-card"> 파일명: </label>

        <input readonly id="id-card" type="text" :value="idImage.name" />
      </v-col>

      <v-col cols="12" class="file-upload__btn" :class="{ float: idImage === null }">
        <FileUploadButton
          :files.sync="idImage"
          :isLoading="uploadFileLoading"
          @update:files="uploadFile"
          singleFile
        >
          {{ idImage ? '다른파일 업로드' : '주민등록증 업로드' }}
        </FileUploadButton>
      </v-col>
    </v-row>

    <v-row class="mt-3" no-gutters>
      <v-col cols="12" class="mb-4">
        <label class="font-size--16">이름</label>
        <v-text-field
          v-model="idForm.name"
          :rules="rules.name"
          :loading="isLoading"
          :error="!!invalidMsg.name"
          hide-details
        ></v-text-field>
        <ErrorText class="mt-1" :text="invalidMsg.name" />
      </v-col>

      <v-col cols="12" class="mb-4">
        <label class="font-size--16">주민등록번호</label>
        <v-row no-gutters>
          <v-col cols="4">
            <v-text-field
              v-input-restrict.number
              v-model="idForm.jumin1"
              :rules="rules.jumin1"
              :error="!!invalidMsg.jumin1"
              ref="ref-form-jumin1"
              maxlength="6"
              type="tel"
              hide-details
              @input="focusNextForm('jumin1')"
            ></v-text-field>
            <ErrorText class="mt-1" :text="invalidMsg.jumin1" />
          </v-col>

          <v-col cols="1" class="d-flex align-center justify-center">
            <p class="ma-0">ㅡ</p>
          </v-col>

          <v-col cols="7">
            <v-text-field
              v-input-restrict.number
              v-model="idForm.jumin2"
              :append-icon="jumin2Show ? 'mdi-eye' : 'mdi-eye-off'"
              :type="jumin2Show ? 'tel' : 'password'"
              :rules="rules.jumin2"
              :error="!!invalidMsg.jumin2"
              ref="ref-form-jumin2"
              maxlength="7"
              hide-details
              inputmode="numeric"
              @click:append="jumin2Show = !jumin2Show"
              @input="focusNextForm('jumin2')"
            ></v-text-field>
            <ErrorText class="mt-1" :text="invalidMsg.jumin2" />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" class="mb-8">
        <label class="font-size--16">발급일자</label>
        <v-text-field
          v-input-restrict.number
          v-model="idForm.issueDate"
          :rules="rules.issueDate"
          ref="ref-form-issueDate"
          type="tel"
          maxlength="8"
          @input="focusNextForm('issueDate')"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <BasicButton @click="save()" :disabled="saveLoading || !isValidAll" :loading="saveLoading">
          인증하기
        </BasicButton>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Regex from '@/const/Regex'
import SkeletonIdLoader from '@/components/common/loading/SkeletonIdLoader.vue'
import FileUploadButton from '@/components/common/form/FileUploadButton.vue'
import { mapActions, mapState } from 'vuex'

export default {
  components: {
    SkeletonIdLoader,
    FileUploadButton,
  },
  data() {
    return {
      isLoading: false,
      saveLoading: false,
      uploadFileLoading: false,
      idImage: null,
      jumin2Show: false,
      idForm: {
        name: null,
        jumin1: null,
        jumin2: null,
        issueDate: null,
      },
      invalidMsg: {
        name: null,
        jumin1: null,
        jumin2: null,
        issueDate: null,
      },
      cddForm: {
        jumin: null,
        name: null,
        issueDate: null,
        cddFile: null,
      },
      rules: {
        name: [
          (value) => !!value || '이름을 입력해주세요.',
          (value) => Regex.NAME.REGEX.test(value) || Regex.NAME.MSG,
        ],
        jumin1: [
          (value) => !!value || '생년월일을 입력해주세요.',
          (value) => Regex.JUMIN1.REGEX.test(value) || Regex.JUMIN1.MSG,
        ],
        jumin2: [
          (value) => !!value || '주민등록번호를 입력해주세요.',
          (value) => Regex.JUMIN2.REGEX.test(value) || Regex.JUMIN2.MSG,
        ],
        issueDate: [
          (value) => !!value || '발급일자를 입력해주세요.',
          (value) => Regex.DATE.REGEX.test(value) || Regex.DATE.MSG,
        ],
      },
    }
  },
  computed: {
    ...mapState('auth', ['me']),
    formKeys() {
      return Object.keys(this.idForm)
    },
    resident() {
      const { jumin1, jumin2 } = this.idForm
      const issuedate = this.idForm.issueDate
        ? this.idForm.issueDate.replace(/^(\d{0,4})(\d{0,2})(\d{0,2})$/g, '$1-$2-$3')
        : null
      return {
        jumin: jumin1 + jumin2,
        issueDate: issuedate,
        image: this.idImage,
      }
    },
    idFormArr() {
      const arr = ['jumin1', 'jumin2']
      // for (const k of this.serialForm) {
      //   arr.push(k.name)
      // }
      arr.push('issueDate')
      return arr
    },
    isValidAll() {
      if (!this.idImage) {
        return false
      }

      return Object.keys(this.idForm).every((idKey) => {
        return !!this.idForm?.[idKey] && !this.invalidMsg?.[idKey]
      })
    },
  },
  watch: {
    'me': {
      deep: true,
      immediate: true,
      handler(v) {
        this.idForm.name = v.name
      },
    },
    'idForm.name': {
      immediate: true,
      handler() {
        this.validCheck('name')
      },
    },
  },
  methods: {
    ...mapActions('kyc', ['addResidentRegistrationCertificate']),
    ...mapActions('document', ['addDocuments']),
    ...mapActions('auth', ['getMe']),
    getFormErrMsg(type, newVal) {
      const rules = this.rules[type]
      let msg = null
      if (newVal !== null) {
        for (let index = 0; index < rules.length; index++) {
          const rule = rules[index]
          const isValid = rule(newVal)
          if (typeof isValid !== 'boolean') {
            msg = isValid
            break
          }
        }
      }
      return msg
    },
    validCheck(property) {
      const prop = this.idForm?.[property]
      const msg = this.getFormErrMsg(property, prop)
      this.invalidMsg[property] = msg
    },
    valueIsValid(property) {
      const value = this.idForm?.[property]
      const invalidMsg = this.invalidMsg?.[property]
      return !!value && !invalidMsg
    },
    focusNextForm(property) {
      this.validCheck(property)
      const isValid = this.valueIsValid(property)
      if (isValid) {
        const valueIdx = this.formKeys.indexOf(property)
        if (valueIdx > -1 && valueIdx < this.formKeys.length - 1) {
          const nextRefKey = this.formKeys[valueIdx + 1]
          let nextRef = this.$refs[`ref-form-${nextRefKey}`]
          if (Array.isArray(nextRef)) {
            nextRef = nextRef[0]
          }
          nextRef?.$refs?.input.focus()
        }
      }
    },
    async uploadFile() {
      try {
        this.uploadFileLoading = true
        await this.addDocuments({
          key: 'identification',
          file: this.idImage,
        })
      } finally {
        this.uploadFileLoading = false
      }
    },
    async save() {
      if (!this.isValidAll) {
        return
      }

      const { isConfirmed } = await this.$swal.basic.confirm('주민등록증 인증을 신청합니다.')

      if (!isConfirmed) {
        return
      }

      try {
        this.saveLoading = true
        const { message, result } = await this.addResidentRegistrationCertificate(this.resident)
        // i
        if (result === true) {
          this.$emit('success', { message, result })
        }
      } finally {
        this.saveLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.file-upload {
  position: relative;
  margin-bottom: 15px;
  text-align: center;

  input {
    min-width: 200px;
    max-width: 280px;
  }

  &__btn {
    &.float {
      position: absolute;
      display: flex;
      height: 206px;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
